/**
 * Shared cabinet furniture (ART-BIBLE §10 "silhouette first").
 *
 * Every cabinet in SPIELHÖLLE is assembled from these parts, which is *why* five very
 * different machines read as one arcade: same trim, same board, same tray, same gauge,
 * same grime. Add a sixth machine out of these parts and it will look native.
 */

import {
  Group, Mesh, BoxGeometry, PlaneGeometry, CylinderGeometry, TorusGeometry, SphereGeometry,
  MeshBasicMaterial, DoubleSide, Color, Vector3, AdditiveBlending, Object3D,
  CanvasTexture, SRGBColorSpace,
} from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import {
  CREME, BAKELIT, SENF, NACHT, INK, PAPIER, NEON, BLUTROT, MATERIALS,
  css, mixHex, type Triple, type NeonName,
} from '../art/palette';
import { toon, toonChassis, toonMetal, toonPlastic, toonCavity } from '../art/toon';
import { grime, label, printed, FONT_SIGN, FONT_MONO, pool, halo } from '../art/textures';
import { neonTube, floorPool } from '../art/neon';
import { decalPlate } from '../art/atlas';

// ---------------------------------------------------------------- geometry

/**
 * Chamfered box. Sharp boxes are the enemy of cel shading: a chamfer gives the ramp a
 * narrow band to sit in and turns a flat panel into a drawn edge.
 */
export function chamferBox(w: number, h: number, d: number, radius = 0.35, seg = 2): RoundedBoxGeometry {
  return new RoundedBoxGeometry(w, h, d, seg, Math.min(radius, Math.min(w, h, d) / 2.2));
}

export interface ChassisOpts {
  seed?: number;
  wear?: [number, number, number][];
  radius?: number;
  ink?: number;
}

/** the body of a machine: chamfered, grimy, inked */
export function chassis(w: number, h: number, d: number, tone: Triple, opts: ChassisOpts = {}): Mesh {
  const geo = chamferBox(w, h, d, opts.radius ?? 0.4);
  const mat = toonChassis(tone.base, grime({ seed: opts.seed ?? 3, wear: opts.wear }));
  const m = new Mesh(geo, mat);
  m.castShadow = true;
  m.receiveShadow = true;
  m.userData.ink = opts.ink ?? 2.4;
  m.userData.warmInk = true;
  return m;
}

/** dark interior backing — cabinet mouths, behind glass, inside the reel drum */
export function cavity(w: number, h: number, d: number): Mesh {
  const m = new Mesh(new BoxGeometry(w, h, d), toonCavity(NACHT.void));
  m.userData.noInk = true;
  return m;
}

/** glass: never outlined, one white streak, barely there */
export function glass(w: number, h: number, tint = 0x9fd6e8): Mesh {
  const mat = new MeshBasicMaterial({
    color: new Color(tint), transparent: true, opacity: 0.14,
    depthWrite: false, side: DoubleSide, toneMapped: false,
  });
  const m = new Mesh(new PlaneGeometry(w, h), mat);
  m.userData.noInk = true;
  m.renderOrder = 3;
  // the streak: one hard diagonal highlight, the whole reason glass reads as glass
  const streak = new Mesh(new PlaneGeometry(w * 0.10, h * 1.5), new MeshBasicMaterial({
    color: new Color(0xffffff), transparent: true, opacity: 0.035,
    blending: AdditiveBlending, depthWrite: false, toneMapped: false,
  }));
  streak.position.set(-w * 0.22, 0, 0.02);
  streak.rotation.z = 0.36;
  streak.userData.noInk = true;
  streak.userData.tier = 1;
  m.add(streak);
  return m;
}

// ---------------------------------------------------------------- marquee

export interface MarqueeOut {
  group: Group;
  /** the lightbox panel — flickers with the machine's mood */
  setIntensity(v: number): void;
}

/**
 * The lightbox marquee every cabinet wears: backlit name panel + a neon tube border +
 * a light pool on the floor. Three draws for the panel, two for the tube.
 */
export function marquee(text: string, w: number, h: number, accent: NeonName, subtitle?: string): MarqueeOut {
  const g = new Group();

  const tex = label(subtitle ? [text, subtitle] : [text], {
    font: FONT_SIGN, weight: '600', size: 96, color: INK, bg: mixHex(CREME.lit, NEON[accent].halo, 0.25),
    letterSpacing: '0.10em', padding: 34,
  });
  const panelMat = new MeshBasicMaterial({ map: tex, toneMapped: false });
  const panel = new Mesh(new PlaneGeometry(w, h), panelMat);
  panel.userData.noInk = true;
  panel.position.z = 0.38;

  // the frame that holds it
  const frame = new Mesh(chamferBox(w + 0.9, h + 0.9, 0.7, 0.2), toonChassis(BAKELIT.base, grime({ seed: 9 })));
  frame.userData.ink = 2.4; frame.userData.warmInk = true;
  frame.castShadow = true;

  // spill: the marquee lights its own face
  const spill = new Mesh(new PlaneGeometry(w * 1.15, h * 2.4), new MeshBasicMaterial({
    map: halo(2.6), color: new Color(NEON[accent].halo), transparent: true,
    blending: AdditiveBlending, depthWrite: false, opacity: 0.10, toneMapped: false,
  }));
  spill.position.set(0, -h * 0.75, 0.52);
  spill.userData.noInk = true;
  spill.userData.tier = 1;

  const tube = neonTube([
    new Vector3(-w / 2 - 0.3, -h / 2 - 0.35, 0.46),
    new Vector3(w / 2 + 0.3, -h / 2 - 0.35, 0.46),
  ], { color: accent, radius: 0.10, sickness: 0.05 });

  g.add(frame, panel, spill, tube.group);
  return {
    group: g,
    setIntensity(v: number) {
      panelMat.color.setScalar(0.25 + 0.75 * v);
      (spill.material as MeshBasicMaterial).opacity = 0.10 * v;
      tube.setIntensity(v);
    },
  };
}

// ---------------------------------------------------------------- Hausregeln

export interface RuleLineLike { text: string; source: string; discovered: boolean }

/**
 * The Hausregeln board — the room IS the character sheet, so this has to be readable
 * from the aisle. Cream card, ink type, red crooked stamp for undiscovered ZINKEN.
 */
export function hausregelnBoard(w: number, h: number, lines: RuleLineLike[]): Mesh {
  const S = 512;
  const c = document.createElement('canvas');
  c.width = S; c.height = Math.round(S * (h / w));
  const g = c.getContext('2d')!;

  g.fillStyle = css(PAPIER);
  g.fillRect(0, 0, c.width, c.height);
  g.strokeStyle = css(INK, 0.5);
  g.lineWidth = 4;
  g.strokeRect(9, 9, c.width - 18, c.height - 18);

  g.fillStyle = css(INK);
  g.font = `600 30px ${FONT_SIGN}`;
  g.textBaseline = 'middle';
  g.letterSpacing = '0.18em';
  g.fillText('HAUSREGELN', 26, 40);
  g.letterSpacing = '0em';
  g.strokeStyle = css(INK, 0.35);
  g.lineWidth = 2;
  g.beginPath(); g.moveTo(24, 60); g.lineTo(c.width - 24, 60); g.stroke();

  let y = 88;
  lines.slice(0, 7).forEach((l, i) => {
    g.font = `20px ${FONT_MONO}`;
    g.fillStyle = css(INK, 0.55);
    g.fillText(`${i + 1}.`, 26, y);
    g.font = `23px ${FONT_SIGN}`;
    g.fillStyle = css(l.discovered ? INK : mixHex(INK, PAPIER, 0.45));
    const txt = l.discovered ? l.text : '?????????????';
    g.fillText(txt, 62, y);
    if (l.source === 'zinken') {
      // the House's crooked stamp
      g.save();
      g.translate(c.width - 66, y);
      g.rotate(-0.22);
      g.strokeStyle = css(BLUTROT.base, 0.85);
      g.lineWidth = 3;
      g.strokeRect(-26, -14, 52, 28);
      g.fillStyle = css(BLUTROT.base, 0.85);
      g.font = `600 18px ${FONT_SIGN}`;
      g.textAlign = 'center';
      g.fillText('ZINKEN', 0, 1);
      g.textAlign = 'left';
      g.restore();
    }
    y += 34;
  });

  // the board is read from the aisle — it is printed matter and follows the same policy
  const t = printed(new CanvasTexture(c));
  t.colorSpace = SRGBColorSpace;

  const mat = toon({ color: 0xffffff, ramp: 'SOFT2', rim: 0.1, map: t, halftone: 0 });
  const card = new Mesh(new PlaneGeometry(w, h), mat);
  card.position.z = 0.22;
  card.userData.noInk = true;

  // the board is a physical thing bolted to the cabinet, not a floating card
  const back = new Mesh(chamferBox(w + 0.7, h + 0.7, 0.4, 0.12), toonChassis(BAKELIT.shade, grime({ seed: 31 })));
  back.userData.ink = 2.2;
  back.userData.warmInk = true;
  back.castShadow = true;
  back.add(card);
  return back;
}

// ---------------------------------------------------------------- gauges

export interface Gauge { group: Group; set(v01: number): void }

/** DER TOPF — the near-miss pot. Amber column that fills; "almost" is banked, visibly. */
export function topfGauge(h = 5): Gauge {
  const g = new Group();
  const tubeGeo = new CylinderGeometry(0.34, 0.34, h, 10, 1, true);
  const shell = new Mesh(tubeGeo, new MeshBasicMaterial({
    color: new Color(0x8fb9c9), transparent: true, opacity: 0.18, side: DoubleSide,
    depthWrite: false, toneMapped: false,
  }));
  shell.userData.noInk = true;

  const fillGeo = new CylinderGeometry(0.27, 0.27, 1, 10);
  const fillMat = new MeshBasicMaterial({ color: new Color(NEON.amber.core), toneMapped: false });
  const fill = new Mesh(fillGeo, fillMat);
  fill.userData.noInk = true;
  fill.userData.dynamic = true;

  const glow = new Mesh(new PlaneGeometry(2.0, h * 1.15), new MeshBasicMaterial({
    map: halo(1.7), color: new Color(NEON.amber.halo), transparent: true,
    blending: AdditiveBlending, depthWrite: false, opacity: 0.3, toneMapped: false,
  }));
  glow.userData.noInk = true;
  glow.userData.dynamic = true;
  glow.userData.tier = 1;

  const cap = new Mesh(new CylinderGeometry(0.42, 0.42, 0.3, 10), toonMetal(MATERIALS.silber.base));
  cap.position.y = h / 2 + 0.1; cap.userData.ink = 1.8;
  const foot = cap.clone(); foot.position.y = -h / 2 - 0.1;

  g.add(shell, fill, glow, cap, foot);
  return {
    group: g,
    set(v: number) {
      const k = Math.max(0.001, Math.min(1, v));
      fill.scale.y = k * h;
      fill.position.y = -h / 2 + (k * h) / 2;
      (glow.material as MeshBasicMaterial).opacity = 0.10 + 0.34 * k;
    },
  };
}

/** TILT bubble — a spirit level in a dome. Calm cyan → hot red at lockout. */
export function tiltBubble(): Gauge {
  const g = new Group();
  g.userData.tier = 1;
  const dome = new Mesh(new SphereGeometry(0.62, 14, 10, 0, Math.PI * 2, 0, Math.PI / 2),
    new MeshBasicMaterial({ color: new Color(0xa8cfdd), transparent: true, opacity: 0.2, depthWrite: false, toneMapped: false }));
  dome.userData.noInk = true;
  const ring = new Mesh(new TorusGeometry(0.62, 0.09, 6, 16), toonMetal(MATERIALS.silber.base));
  ring.rotation.x = Math.PI / 2;
  ring.userData.ink = 1.8;
  const bubbleMat = new MeshBasicMaterial({ color: new Color(NEON.cyan.core), toneMapped: false });
  const bubble = new Mesh(new SphereGeometry(0.2, 10, 8), bubbleMat);
  bubble.position.y = 0.18;
  bubble.userData.noInk = true;
  bubble.userData.dynamic = true;
  g.add(dome, ring, bubble);
  return {
    group: g,
    set(v: number) {
      const a = v * Math.PI * 4;
      bubble.position.set(Math.cos(a) * 0.30 * v, 0.18, Math.sin(a) * 0.30 * v);
      bubbleMat.color.set(mixHex(NEON.cyan.core, BLUTROT.lit, Math.min(1, v * 1.2)));
    },
  };
}

// ---------------------------------------------------------------- fittings

/** the coin tray: scuffed metal, always at knee height, always full of nothing */
export function coinTray(w: number, d = 3.2): Group {
  const g = new Group();
  const lip = new Mesh(chamferBox(w, 1.0, d, 0.18), toonMetal(mixHex(MATERIALS.silber.base, BAKELIT.shade, 0.45)));
  lip.userData.ink = 2.0;
  lip.castShadow = true;
  const inner = cavity(w - 0.7, 0.7, d - 0.6);
  inner.position.y = 0.35;
  g.add(lip, inner);
  return g;
}

/** arcade buttons: mustard, red, and one that has been pressed a hundred thousand times */
export function buttonRow(n: number, spacing = 2.0, colors = [SENF.base, BLUTROT.base]): Group {
  const g = new Group();
  for (let i = 0; i < n; i++) {
    const c = colors[i % colors.length];
    const cap = new Mesh(new CylinderGeometry(0.62, 0.68, 0.42, 14),
      toonPlastic(c, mixHex(0x000000, c, 0.12)));
    cap.rotation.x = Math.PI / 2;
    cap.position.x = (i - (n - 1) / 2) * spacing;
    cap.userData.ink = 2.0;
    cap.castShadow = true;
    const collar = new Mesh(new CylinderGeometry(0.82, 0.82, 0.22, 14), toonMetal(MATERIALS.silber.shade));
    collar.rotation.x = Math.PI / 2;
    collar.position.set(cap.position.x, 0, -0.14);
    collar.userData.ink = 1.6;
    g.add(collar, cap);
  }
  return g;
}

/** coin slot + reject button: the ritual entrance to every machine */
export function coinSlot(): Group {
  const g = new Group();
  const plate = new Mesh(chamferBox(2.4, 3.4, 0.35, 0.12), toonMetal(MATERIALS.silber.base));
  plate.userData.ink = 2.0;
  const slot = new Mesh(new BoxGeometry(1.35, 0.28, 0.5), toonCavity(NACHT.void));
  slot.position.set(0, 0.85, 0.16);
  slot.userData.noInk = true;
  const lbl = decalPlate('1 DM', 1.7, { font: FONT_SIGN, weight: '600', size: 48, color: INK, letterSpacing: '0.08em' });
  lbl.position.set(0, -0.55, 0.19);
  g.add(plate, slot, lbl);
  return g;
}

/** stubby chrome legs + a kicked-in kickplate. Cabinets never touch the floor cleanly. */
export function legs(w: number, d: number, h = 1.6): Group {
  const g = new Group();
  const geo = new CylinderGeometry(0.34, 0.42, h, 8);
  const mat = toonMetal(MATERIALS.silber.shade);
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const l = new Mesh(geo, mat);
      l.position.set(sx * (w / 2 - 0.7), h / 2, sz * (d / 2 - 0.7));
      l.userData.ink = 2.0;
      l.castShadow = true;
      g.add(l);
    }
  }
  return g;
}

/** the accumulated stickers of thirty years: TÜV, a band logo, a cigarette burn */
export function stickers(w: number, h: number, seed = 1): Group {
  const g = new Group();
  const notes = [
    { t: 'AB 18', c: BLUTROT.base, s: 0.8 },
    { t: 'GEPRÜFT', c: NACHT.base, s: 0.6 },
    { t: 'NICHT LEHNEN', c: SENF.shade, s: 0.55 },
  ];
  notes.forEach((n, i) => {
    const m = decalPlate(n.t, n.s * 2.6, { font: FONT_SIGN, weight: '600', size: 40, color: PAPIER, bg: n.c, pad: 12, letterSpacing: '0.08em' });
    m.position.set((((seed * 7 + i * 13) % 10) / 10 - 0.5) * w * 0.6, h * (0.18 + i * 0.12) - h / 2, 0.02);
    m.rotation.z = (((seed + i) % 5) - 2) * 0.03;
    g.add(m);
  });
  return g;
}

/** every neon sign drops a pool: this is what makes the room feel lit BY the machines */
export function lightPool(accent: NeonName, radius: number, opacity = 0.42): Mesh {
  return floorPool(accent, radius, pool(), opacity);
}

/**
 * The dark ellipse under a heavy object. Shadow maps give you the cast shadow; this gives
 * you the CONTACT — the reason a cabinet reads as standing on the floor and not hovering
 * one centimetre above it. Cheap, and worth more than another light.
 */
export function contactShadow(w: number, d: number, opacity = 0.55): Mesh {
  const m = new MeshBasicMaterial({
    map: pool(), color: new Color(NACHT.void), transparent: true,
    depthWrite: false, opacity, toneMapped: false,
  });
  const mesh = new Mesh(new PlaneGeometry(w, d), m);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = 0.03;
  mesh.userData.noInk = true;
  mesh.renderOrder = 1;
  return mesh;
}

/** an "AUSSER BETRIEB" banner for a cabinet the House has taken */
export function ausserBetrieb(w: number): Mesh {
  const m = decalPlate('AUSSER BETRIEB', w, {
    font: FONT_SIGN, weight: '600', size: 72, color: PAPIER, bg: BLUTROT.shade,
    letterSpacing: '0.14em', pad: 26,
  });
  m.rotation.z = -0.09;
  return m;
}

export { Group, Mesh, Object3D, Vector3, PlaneGeometry, BoxGeometry, CylinderGeometry, MeshBasicMaterial, Color };
